import { LenderConfiguration } from '@shared/model/lender-config.model';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@cudirect/shared-ui-library';
import { LenderService } from '@shared/service/lender/lender.service';
import { OAORoutingUrl, RoutingUrl } from '@shared/constant/route-const';
import { IConsumerInformation } from '@shared/model/ApiResponses/ConsumerPortal/consumer-information.model';
import { ResponseModel } from '@shared/model/ApiResponses/ConsumerPortal/response-model';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { Observable } from 'rxjs';
import { ConsumerFunding } from 'src/postapp/models/consumer-funding.model';
import { LoanPurposeTypes, LoanType, DecisionStatus } from 'src/shared/enum';
import { Dashboard } from '../../app/modules/main/model/dashboard.model';
import { ESign_api } from 'src/shared/constant/document-esign.const';
import { LDRLoan } from 'src/postapp/consts/ldr-data.const';
import {
  AppSummary,
  ConsumerLoanCollection,
  CollateralDetail,
  PostApplicationTask,
  PostApplicationBanner
} from 'src/postapp/models/application.model';
import { map } from 'rxjs/operators';
import { StatefulService } from '@app/service/stateful.service';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { CustomFunctionsService } from '@workflow/ecp-workflow/dynamic-app/service/logic-functions/custom-functions.service';
import { VisibleFunctionsService } from '@workflow/ecp-workflow/dynamic-app/service/logic-functions/visible-functions.service';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { ImageUrl } from '@workflow/ecp-shared/constant/information.const';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { FlowInfo, getFlowInfo } from '@workflow/helper/flow-utility/flow-utility';
import { FlowInstance, FlowService } from '@workflow/class/flow.service';
import { PageInstance } from '@workflow/class/page.service';
/**
 * Loanservice
 */
@Injectable({ providedIn: 'root' })
export class LoanService {
  /** holds dashBoard application list */
  public dashboardLoans: Dashboard;
  /** holds app summary details */
  public loans: AppSummary[] = [];
  /** Lender Config */
  public lenderConfig: LenderConfiguration;

  public cachedApp: AppSummary[] = [];

  constructor(
    private readonly apiService: ApiService,
    private readonly lenderService: LenderService,
    private readonly bundleHelperService: BundleHelperService,
    private readonly customFunctionService: CustomFunctionsService,
    private readonly visibleFunctionService: VisibleFunctionsService,
    private readonly router: Router,
    private readonly storageWrapperService: StorageWrapperService,
    private readonly userService: UserService,
    private readonly statefulService: StatefulService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly flowService: FlowService
  ) {
    this.lenderConfig = this.lenderService.lenderDataInfo;
  }

  public getLoan(appNumber: string): Observable<AppSummary> {
    return this.apiService.request({
      body: JSON.stringify(appNumber),
      urlObj: LDRLoan.GetLoanDetail
    });
  }

  public getOlaLoan(loanNumber: string): Observable<AppSummary> {
    return this.apiService.request({
      urlObj: LDRLoan.GetLoanOLADetail,
      body: JSON.stringify(loanNumber)
    });
  }

  public getLoanDetails(appNumber: string): Observable<AppSummary> {
    return this.getLoan(appNumber).pipe(
      map((response: AppSummary) => {
        this.processLoanDetails(response);
        return response;
      })
    );
  }

  /**
   * Get Overview Banner data for OLA
   * @param appId string
   * @returns AppSummary
   */
  public GetOLAOverviewBannerData(appId: string): Observable<PostApplicationBanner> {
    return this.apiService.request<PostApplicationBanner>({
      body: JSON.stringify(appId),
      urlObj: LDRLoan.GetOLAOverviewBanner
    });
  }

  public getOLAPostAppTasks(appNumber: string): Observable<PostApplicationTask> {
    return this.getOLAPostAppTasksDetails(appNumber);
  }

  public getOLAPostAppTasksDetails(appNumber: string): Observable<AppSummary> {
    return this.apiService.request({
      body: JSON.stringify(appNumber),
      urlObj: LDRLoan.GetOLAPostAppTaskDetail
    });
  }

  public getOlaLoanDetails(appNumber: string): Promise<AppSummary> {
    return this.getOlaLoan(appNumber)
      .toPromise()
      .then((response: AppSummary) => {
        this.processLoanDetails(response);
        return response;
      });
  }

  public getLoanCollection(): Observable<ConsumerLoanCollection> {
    return this.apiService.request({
      urlObj: LDRLoan.GetLoanCollection
    });
  }

  private processLoanDetails(loan: AppSummary): void {
    loan.imageUrl = this.getCustomImageUrl(loan);
    this.loans.push(loan);
  }

  public getestMonthlyPayment(estMP: string): string {
    let estMonthlyPayment = '';
    if (estMP) {
      estMonthlyPayment = parseFloat(estMP) > 0 ? estMP : estMonthlyPayment;
    }
    return estMonthlyPayment;
  }

  public getCustomImageUrl(loan: AppSummary): string {
    let imageURL: string;

    if (!loan) {
      return ImageUrl.default;
    }
    if (
      loan.loanType === LoanType.Auto ||
      loan.loanType === LoanType.CreditCard ||
      loan.loanType === LoanType.Personal ||
      loan.loanType === LoanType.Secured ||
      loan.loanType === LoanType.PersonalLineOfCredit
    ) {
      /* For Auto Loan */
      if (!loan.imageUrl || loan.imageUrl.length === 0) {
        if (loan.loanType === LoanType.Auto) {
          return ImageUrl.autoDefault;
        } else if (
          loan.loanType === LoanType.Personal ||
          loan.loanType === LoanType.Secured ||
          loan.loanType === LoanType.PersonalLineOfCredit
        ) {
          return ImageUrl.personal;
        } else {
          return ImageUrl.creditCard;
        }
      }
      if (loan.imageUrl && loan.imageUrl.length > 0) {
        return loan.imageUrl;
      }
    } else {
      /* For Other Loans */
      imageURL = this.otherLoanTypeImage(loan);
    }

    return imageURL;
  }

  private otherLoanTypeImage(loan: AppSummary) {
    if (!loan.loanPurpose && (!loan.propertyImageUrls || loan.propertyImageUrls.length === 0)) {
      return ImageUrl.default;
    }

    if (loan.propertyImageUrls && loan.propertyImageUrls.length > 0) {
      return loan.propertyImageUrls[0];
    }
    const loanType = loan.loanType ? loan.loanType.toString() : '';
    switch (loanType.toLowerCase()) {
      case LoanPurposeTypes.Purchase.toLowerCase():
        return ImageUrl.purchase;
      case LoanPurposeTypes.Refinance.toLowerCase():
        return ImageUrl.refinance;
      case LoanPurposeTypes.HELOC.toLowerCase():
        return ImageUrl.heloc;
      case LoanPurposeTypes.HomeEquityLoan.toLowerCase():
        return ImageUrl.homeEquity;
      default:
        return ImageUrl.default;
    }
  }

  public getImageUrl(
    dealerCode: string,
    collateralDetail: CollateralDetail,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback?: (l: any) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errorCallback?: (e: any) => void
  ): void {
    this.apiService.requestApi({
      urlObj: LDRLoan.GetAutoImage,
      body: {
        dealerCode,
        vin: collateralDetail.vin,
        model: collateralDetail.model,
        year: collateralDetail.year,
        make: collateralDetail.make,
        trim: collateralDetail.condition
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      callback: (responseImage: any) => {
        if (responseImage && responseImage.imageUrl && callback) {
          callback(responseImage.imageUrl);
        }
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      errorCallback: (erroResponse: any) => {
        if (errorCallback) {
          errorCallback(erroResponse);
        }
      }
    });
  }

  /**
   * Make API request to set the selected loan (documentId) as current active loan, so all bundles apply to this loan
   * @param loanDocumentId the document ID of the targe loan
   */
  public selectLoan(loanNumber: string): Observable<ResponseModel<IConsumerInformation>> {
    return this.apiService.request({
      body: JSON.stringify(loanNumber),
      urlObj: LDRLoan.SelectLoan
    });
  }

  /**
   * Make API request to set the selected loan (documentId) as current active loan, so all bundles apply to this loan
   * @param loanDocumentId the document ID of the targe loan
   */
  public selectOLALoan(loanNumber: string): Observable<ResponseModel<IConsumerInformation>> {
    return this.apiService.request({
      body: JSON.stringify(loanNumber),
      urlObj: LDRLoan.SelectOLALoan
    });
  }

  public createOLALoan(): Observable<ResponseModel<IConsumerInformation>> {
    const urlObj = LDRLoan.CreateOLALoan;
    return this.apiService.request({
      urlObj,
      urlIds: { olaAuthorization: '' }
    });
  }

  /**
   * Make API request to set the selected loan (documentId) as current active loan, so all bundles apply to this loan
   * @param loanDocumentId the document ID of the targe loan
   */
  public selectOAOLoan(loanNumber: string): Observable<ResponseModel<IConsumerInformation>> {
    return this.apiService.request({
      body: JSON.stringify(loanNumber),
      urlObj: LDRLoan.SelectOAOLoan
    });
  }

  public applyForOAOMembership(loanNumber: string): Observable<ResponseModel<IConsumerInformation>> {
    return this.apiService.request({
      body: JSON.stringify(loanNumber),
      urlObj: LDRLoan.ApplyForMembership
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public submitDisburseFunding(consumerFunding: ConsumerFunding): Observable<ResponseModel<any>> {
    return this.apiService.request<ConsumerFunding>({
      urlObj: LDRLoan.DisburseLoanProceeds,
      body: consumerFunding
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as Observable<any>;
  }

  public redirectToOAO(isApplyforMembership?: boolean): void {
    this.userService.flowType = 'OAO';
    this.userService.isConsumer = false;
    let membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    const isyouth = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').value;
    if (isyouth === true) {
      membershipTypeName = 'youth';
    }
    this.removeLoanDetailsFromLocalStorage();
    this.statefulService.wrap('mem:primary-application-status').value = true;
    if (isApplyforMembership) {
      this.statefulService.wrap('api:oaoMemberInformation/isEligibilityValidated').value = true;
    }

    this.refreshBundle(isApplyforMembership, membershipTypeName, isyouth);
  }

  private refreshBundle(isApplyforMembership: boolean, membershipTypeName: string, isyouth: PrimitiveType): void {
    const appNumber = this.storageWrapperService.get<string>('appNumber');
    this.bundleHelperService.refreshBundle(() => {
      const membershipTypeId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeID').value;
      const membershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
      const eligibility = this.statefulService.wrap('api:oaoMemberInformation/isEligibilityValidated').value;
      const alreadyMember = this.statefulService.wrap('api:oaoMemberInformation/alreadyMember').value;
      this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value = membershipId;
      if (isApplyforMembership) {
        this.statefulService.wrap('api:oaoMemberInformation/hasRelatedApplication').value = true;
        if (appNumber) {
          this.statefulService.wrap('api:oaoMemberInformation/relatedApplicationId').value = appNumber;
        }
        this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillOption').value = 'Manual';
      } else {
        if (!membershipTypeId) {
          this.router.navigate(['/oao/membership']);
          return;
        }

        const decisionStatus = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationStatus').value;
        if (decisionStatus && decisionStatus.toString().toLowerCase() === DecisionStatus.Declined) {
          this.router.navigate([this.getCompleteRoutePath(membershipTypeName, RoutingUrl.OaoAccountService)]);
          return;
        }
      }

      this.validatedDOB();
      this.setDefaultValues();

      this.redirectoOAO(isApplyforMembership, membershipTypeName, membershipTypeId, isyouth, eligibility, alreadyMember);
    });
  }

  private setDefaultValues(): void {
    this.statefulService.wrap('ss:oao-first-time-navigation').value = true;
    this.statefulService.wrap('ss:oao-primary-applicant-prefill-alert-success').value = null;
    this.statefulService.wrap('ss:oao-primary-co-applicant-prefill-alert-success').value = null;
    this.statefulService.wrap('ss:oao-primary-additional-co-applicant-prefill-alert-success').value = null;
    this.statefulService.wrap('api:oaoMemberInformation/showPreferredbankbranches').value = true;
    this.statefulService.wrap('api:oaoMemberInformation/showAdditionalQuestions').value = true;

    if (this.featureFlagService.isFeatureEnabled('SaveAppDataToSessionStorage')) {
      // Preparing the Session Storage variables for any new Membership application created from the PostApp:Tasks
      this.clearSessionStorageForNewApp();
    }
  }

  private validatedDOB(): void {
    this.validatePrimaryApplicantDOB();
    this.validateCoApplicantDOB();
    this.validateAdditionalApplicantDOB();
  }

  /** validate Primay Applicant DOB */
  private validatePrimaryApplicantDOB(): void {
    // Handling OAO application in-memory fields
    const autoFillOption = this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/autoFillOption`).value;
    if (autoFillOption && autoFillOption !== '' && autoFillOption === 'MobileNumber') {
      const dateOfBirth = this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/dateOfBirth`).valueString;
      this.statefulService.wrap('mem:primary-application-dob-valid').value = this.customFunctionService.checkDOBValid(dateOfBirth);
    } else {
      this.statefulService.wrap('mem:primary-application-dob-valid').value = false;
    }
  }

  /** validate Co Applicant DOB */
  private validateCoApplicantDOB(): void {
    const coApplicantAutoFillOption = this.statefulService.wrap(`api:oaoMemberInformation/coApplicant/autoFillOption`).value;
    if (coApplicantAutoFillOption && coApplicantAutoFillOption !== '' && coApplicantAutoFillOption === 'MobileNumber') {
      const dateOfBirth = this.statefulService.wrap(`api:oaoMemberInformation/coApplicant/dateOfBirth`).valueString;
      this.statefulService.wrap('mem:primary-application-co-applicant-dob-valid').value =
        this.customFunctionService.checkDOBValid(dateOfBirth);
    } else {
      this.statefulService.wrap('mem:primary-application-co-applicant-dob-valid').value = false;
    }
  }

  /** validate Additional Applicant DOB */
  private validateAdditionalApplicantDOB(): void {
    const additionalCoApplicantAutoFillOption = this.statefulService.wrap(
      `api:oaoMemberInformation/additionalCoApplicant/autoFillOption`
    ).value;
    if (additionalCoApplicantAutoFillOption === 'MobileNumber') {
      const dateOfBirth = this.statefulService.wrap(`api:oaoMemberInformation/additionalCoApplicant/dateOfBirth`).valueString;
      this.statefulService.wrap('mem:primary-application-additional-co-applicant-dob-valid').value =
        this.customFunctionService.checkDOBValid(dateOfBirth);
    } else {
      this.statefulService.wrap('mem:primary-application-additional-co-applicant-dob-valid').value = false;
    }
  }

  private redirectoOAO(
    isApplyforMembership: boolean,
    membershipTypeName: string,
    membershipTypeId: PrimitiveType,
    isyouth: PrimitiveType,
    eligibility: PrimitiveType,
    alreadyMember: PrimitiveType
  ): void {
    if (isApplyforMembership) {
      this.router.navigateByUrl(RoutingUrl.OaoMembership);
      return;
    }

    const redirectURL = this.statefulService.wrap('api:oaoMemberInformation/membership/redirectURL').valueString;
    const hasApplicationCheckpointPass = this.statefulService.wrap('api:oaoMemberInformation/hasApplicationCheckpointPass')
      .value as boolean;

    let oaoAppPath = this.getOAOAppPath(membershipTypeName, hasApplicationCheckpointPass, isyouth);
    this.cleanPath(oaoAppPath);
    const flow = this.flowService.createFlow(redirectURL || `oao/${membershipTypeName.toLowerCase()}`);
    // TODO: This was bombing in some cases because flow.config.path is optional in the type but required by getFlowInfo.
    // I added a check in WW for that and returned null for the path in that scenario, but it's unclear what might
    // happen downstream. In addition, this method can return an empty object if no id is passed, which might also break.
    const flowInfo = getFlowInfo(flow);
    const objFirstInvalidPage = this.getOAOFirstInvalidPage(flow, flowInfo, membershipTypeName, redirectURL);

    if (objFirstInvalidPage) {
      console.error('Loopback - Redirecting to first incomplete page:', objFirstInvalidPage);
      this.router.navigate([objFirstInvalidPage]);
      return;
    }

    const incompletedPages = flow.pages ? flow.pages.filter((r) => !r.helper.isPageComplete && r.id !== 'oao-membership') : undefined;
    if (incompletedPages && incompletedPages.length > 0 && incompletedPages[0]) {
      oaoAppPath = `${flowInfo.configPath}/${incompletedPages[0].path}`;
      console.error('Loopback - Redirecting to first incomplete page:', oaoAppPath);
    }
    if (membershipTypeId && (eligibility || (!eligibility && alreadyMember))) {
      this.router.navigate([oaoAppPath]);
      return;
    }

    this.router.navigate([RoutingUrl.OaoMembership]);
  }

  private getOAOAppPath(membershipTypeName: string, hasApplicationCheckpointPass: boolean, isyouth: PrimitiveType): string {
    if (hasApplicationCheckpointPass) {
      return this.getCompleteRoutePath(membershipTypeName, RoutingUrl.OaoAccountService);
    }

    if (this.visibleFunctionService && membershipTypeName && this.visibleFunctionService.isNotBusinessMembership(null)) {
      return this.getCompleteRoutePath(membershipTypeName, isyouth ? RoutingUrl.OaoYouthApplication : RoutingUrl.OaoApplication);
    }

    return this.getCompleteRoutePath(membershipTypeName, RoutingUrl.OaoBusinessApplication);
  }

  private getCompleteRoutePath(membershipType: string, landingpage: string) {
    const redirectURL = this.statefulService.wrap('api:oaoMemberInformation/membership/redirectURL').valueString;
    if (redirectURL?.length > 0) {
      return redirectURL.concat('/') + landingpage;
    }
    return OAORoutingUrl.OAO + membershipType.toLowerCase().concat('/') + landingpage;
  }

  private cleanPath(url: string): string {
    const path = url.startsWith('/') || url.startsWith('\\') ? url.substr(1) : url;
    return path.split('?')[0].split('#')[0];
  }

  private getFirstIncompletePagePath(flowInfo: FlowInfo, page: PageInstance): string {
    const requiredPages = page.helper.getRequiredPages(flowInfo.pages);
    if (requiredPages.length === 0) {
      return '';
    }

    const firstIncompletePage = requiredPages.find((r) => !r.helper.isPageComplete);
    if (firstIncompletePage) {
      return `${flowInfo.configPath}/${firstIncompletePage.path}`;
    }
  }

  private removeLoanDetailsFromLocalStorage(): void {
    this.storageWrapperService.remove('AllowedRoute');
    this.storageWrapperService.remove('appNumber');
  }

  public getESignDocument(envelopId: string): Observable<Blob> {
    return this.apiService.downloadFile({
      urlObj: LDRLoan.DownLoadESignDocument,
      urlIds: { envelopId }
    });
  }

  public getOAOFirstInvalidPage(flow: FlowInstance, flowInfo: FlowInfo, membershipTypeName: string, workflowPrefix): string {
    let objFirstInvalidPage = '';
    if (flow.id.toLowerCase() === (workflowPrefix || `oao/${membershipTypeName.toLowerCase()}`)) {
      const incompletedPages = flow.pages.filter((t) => t.visible && !t.helper.isPageComplete && t.id !== 'oao-membership');
      if (incompletedPages && incompletedPages.length > 0) {
        objFirstInvalidPage = `${flowInfo.configPath}/${incompletedPages[0].path}`;
      }
    }
    return objFirstInvalidPage;
  }

  private GetDocumentDetails(lenderCode: string, docRepID: string): Observable<Blob> {
    return this.apiService.getImage({ urlIds: { lenderCode, docRepID }, urlObj: ESign_api.DocumentDetails });
  }
  public getDocumentByDocRepID(docRepID: string): Promise<string> {
    let pdfBlob: Blob;
    const lenderCode: string = this.lenderService.lenderDataInfo.consumerLoanCode;
    return this.GetDocumentDetails(lenderCode, docRepID)
      .toPromise()
      .then((blob: Blob) => {
        pdfBlob = blob;
        const blobURL = window.URL.createObjectURL(pdfBlob);
        Promise.resolve(true);
        return blobURL;
      });
  }

  /**
   * Method to clear out the Session Storage variables for any new Loan/Membership application created from PostApp:Dashboard/Overview:Task
   */
  public clearSessionStorageForNewApp(): void {
    this.statefulService.wrap('ss:fresh-app-from-postapp').value = true;
    this.statefulService.wrap('ss:current-patch-request-body').value = null;
  }
}
